import { useState, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { useProfileStore } from '@/stores/profileStore'
import { supabase } from '@/lib/supabase'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile'

const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY

type PendingAction = { type: 'signIn' } | { type: 'resetPassword'; email: string }

export function SignInPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { signIn, isLoading, error, clearError } = useAuthStore()
  const { fetchProfile } = useProfileStore()
  const turnstileRef = useRef<TurnstileInstance>(null)
  const pendingActionRef = useRef<PendingAction>({ type: 'signIn' })
  const emailRef = useRef('')
  const passwordRef = useRef('')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)

  // Keep refs in sync with state so callbacks always have current values
  emailRef.current = email
  passwordRef.current = password

  // Get redirect path from location state
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname ?? '/u/dashboard'

  const doSignIn = async (token: string) => {
    const { error } = await signIn(emailRef.current, passwordRef.current, token)

    if (error) {
      setCaptchaToken(null)
      turnstileRef.current?.reset()
    } else {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        await fetchProfile(user.id)
      }
      navigate(from, { replace: true })
    }
  }

  const doResetPassword = async (token: string, resetEmail: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/auth/callback`,
      captchaToken: token,
    })
    setCaptchaToken(null)
    turnstileRef.current?.reset()
    if (error) {
      setFormError('Failed to send reset email. Please try again.')
    } else {
      setFormError(null)
      alert('Check your email for a password reset link.')
    }
  }

  const handleTurnstileSuccess = (token: string) => {
    setCaptchaToken(token)
    setFormError(null)
    const action = pendingActionRef.current
    if (action.type === 'signIn') {
      doSignIn(token)
    } else {
      doResetPassword(token, action.email)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    clearError()
    setFormError(null)

    // Basic validation
    if (!email.trim()) {
      setFormError('Email is required')
      return
    }
    if (!password) {
      setFormError('Password is required')
      return
    }

    pendingActionRef.current = { type: 'signIn' }
    if (captchaToken) {
      await doSignIn(captchaToken)
    } else {
      turnstileRef.current?.execute()
    }
  }

  const handleForgotPassword = () => {
    const resetEmail = prompt('Enter your email address:')
    if (!resetEmail?.trim()) return

    pendingActionRef.current = { type: 'resetPassword', email: resetEmail.trim() }
    setCaptchaToken(null)
    turnstileRef.current?.reset()
    // Small delay to let Turnstile reset before executing again
    setTimeout(() => turnstileRef.current?.execute(), 100)
  }

  return (
    <div
      className="bg-[#FBF9F5] rounded-3xl p-8 border border-[#E3DED2]"
      style={{ boxShadow: '0 6px 18px rgba(15, 28, 46, 0.08)' }}
    >
      <h1 className="text-[30px] font-bold text-[#0F1C2E] mb-2">Log In</h1>
      <p className="text-[#6E7582] text-base mb-8">Welcome back</p>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Cloudflare Turnstile (invisible — triggered on submit) */}
        <Turnstile
          ref={turnstileRef}
          siteKey={TURNSTILE_SITE_KEY}
          onSuccess={handleTurnstileSuccess}
          onError={() => {
            setCaptchaToken(null)
            setFormError('Verification failed. Please try again.')
          }}
          onExpire={() => {
            setCaptchaToken(null)
          }}
          options={{ theme: 'light', size: 'invisible', execution: 'execute' }}
        />

        {/* Email field */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-[#6E7582] mb-2">
            Email address
          </label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full px-4 py-3.5 bg-[#FBF9F5] border border-[#E3DED2] rounded-2xl text-[#0F1C2E] placeholder-[#A4A9B3] outline-none transition-colors focus:border-[#4A6484] focus:ring-2 focus:ring-[#4A6484]/20"
            placeholder="you@example.com"
            disabled={isLoading}
            autoComplete="email"
          />
        </div>

        {/* Password field */}
        <div>
          <label htmlFor="password" className="block text-sm font-medium text-[#6E7582] mb-2">
            Password
          </label>
          <div className="relative">
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-3.5 pr-12 bg-[#FBF9F5] border border-[#E3DED2] rounded-2xl text-[#0F1C2E] placeholder-[#A4A9B3] outline-none transition-colors focus:border-[#4A6484] focus:ring-2 focus:ring-[#4A6484]/20"
              placeholder="Enter your password"
              disabled={isLoading}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-[#6E7582] hover:text-[#0F1C2E] transition-colors"
              tabIndex={-1}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>
        </div>

        {/* Error message */}
        {(formError || error) && (
          <div className="p-3 bg-[#C7796B]/10 border border-[#C7796B] rounded-2xl text-[#C7796B] text-sm">
            {formError || error}
          </div>
        )}

        {/* Submit button */}
        <button
          type="submit"
          disabled={isLoading}
          className="w-full h-[52px] bg-[#D4AF5A] text-[#0F1C2E] font-semibold rounded-full hover:bg-[#C09A4E] focus:ring-2 focus:ring-[#D4AF5A]/40 focus:ring-offset-2 focus:ring-offset-[#FBF9F5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Logging in...
            </>
          ) : (
            'Log In'
          )}
        </button>
      </form>

      {/* Forgot password */}
      <div className="mt-5 text-center">
        <button
          type="button"
          onClick={handleForgotPassword}
          className="text-sm text-[#4A6484] hover:underline transition-colors"
        >
          Forgot your password?
        </button>
      </div>

      {/* Create account link */}
      <div className="mt-8 pt-6 border-t border-[#E3DED2] text-center">
        <p className="text-[#6E7582]">
          Don't have an account?{' '}
          <Link to="/auth/sign-up" className="text-[#D4AF5A] font-medium hover:text-[#C09A4E] transition-colors">
            Create account
          </Link>
        </p>
      </div>
    </div>
  )
}
